import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";

import { Point, BLEND_MODES, Container, Graphics, Sprite } from "pixi.js";
import { Sequence } from "@nolimitcity/slot-game/bin/core/displayobject/Sequence";
import { TimelineMax, TweenLite, Power2 } from "gsap";
import { StateHandler } from "@nolimitcity/slot-game/bin/core/utils/StateHandler";
import { PunkEngineManager } from "../../../punkEngine/PunkEngineManager";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";
import { MathHelper } from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import { PunkSymbolData } from "../../../punkEngine/config/PunkSymbolData";

export class ReelAnimationState extends StateHandler{
    
    public static readonly NORMAL:string = "Reel6";
    public static readonly ATTENTION_A:string = "FX_ReelAttention_A/Loop_A_";
    public static readonly ATTENTION_B:string = "FX_Reel6Attention_B/Loop_B_";
    public static readonly END:string = "FX_Reel6Intro/R6intro_";
    public static readonly BOOM:string = "FX_Xplod/Xplod_";

    public static readonly FIRE:string = "FX_Reel_Att_Fire/Reel_Att_Fire_";
    public static readonly ELECTRIC:string = "FX_Reel_Electric/FX_Electric_";
    public static readonly SWEEP:string = "FX_Reel_Sweep/FX_Sweep_";

    public static readonly REEL_LOCK:string = "Reel_Lock_Intro/Reel_Lock_Intro_";
    public static readonly REEL_ELECTRIC:string = "Reel_Electric";


    // SingleTextures
    public static readonly REEL_FEATURE_ATTENTION:string = "Reel_Attention";
}

export class ReelAnimationController extends Container {

    protected _skullSpritePath:string = "Wild/Skull_Reel";

    protected _areaAnimation:Sequence;
    protected _loopAnimation:Sequence;
    protected _areaOverlayAnimation:Sequence;
    protected _reelID:number = 0;
    protected timeline:TimelineMax;

    protected timelineIdle:TimelineMax;
    protected idleAnimation:Sequence;
    protected idleActive:boolean = false;

    protected _graphicBg:Graphics;

    protected spineAnimation:PIXI.spine.Spine;
    protected _animationState:ReelAnimationState = new ReelAnimationState(ReelAnimationState.NORMAL);
    protected skulls:Sprite[] = [];


    protected get currentAnimationTexture():PIXI.Texture[]{
        return GameResources.getTextures(this._animationState.state);
    }

    public get ActiveSkulls():Sprite[]{
        return this.skulls.filter((skull) => {return skull.alpha != 0});
    }

    protected get stateBlend():number{
        switch(this._animationState.state){
            case ReelAnimationState.BOOM:
            case ReelAnimationState.FIRE:
            case ReelAnimationState.ELECTRIC:
            case ReelAnimationState.SWEEP:
            case ReelAnimationState.REEL_FEATURE_ATTENTION:
                return BLEND_MODES.ADD;
            default:
                return BLEND_MODES.NORMAL;
        }
    }

    protected get stateType():PIXI.Point{
        switch(this._animationState.state){
            case ReelAnimationState.SWEEP:
                return new PIXI.Point(2,2);
            default:
                return new PIXI.Point(1,1);
        }
    }

    public get ReelID():number{
        return this._reelID;
    }

    constructor(reelID:number){
        super();

        this._reelID = reelID;
        this.init();
    }

    protected init():void{
        this.spineAnimation = new PIXI.spine.Spine(GameResources.getSpineAsset("reel6Boom"));
        this.spineAnimation.visible = false;
        this._areaAnimation = Sequence.createSequence(this.currentAnimationTexture);
        this._loopAnimation = Sequence.createSequence(undefined);

        this._areaOverlayAnimation = Sequence.createSequence(undefined);
        this._areaOverlayAnimation.blendMode = BLEND_MODES.ADD;
        this._areaOverlayAnimation.scale.set(2,2);

        this._graphicBg = this.createGraphic();
        this.addChild(this._graphicBg);
        this._graphicBg.alpha = 0;
        if(this.ReelID == 5){
            this._graphicBg.alpha = 1;
            this.addChild(this._loopAnimation);
            for(let i = 0; i < 4; i++){
                let sp:Sprite = Helper.createSprite(GameResources.getTextures(this._skullSpritePath)[0]);
                sp.position.y = PunkSymbolData.DEFAULT_SYMBOL.DIM.HEIGHT - (PunkSymbolData.DEFAULT_SYMBOL.DIM.HEIGHT*0.7)*i;
                this.addChild(sp);
                this.skulls.push(sp);
            }
        }

        this.addChild(this._areaAnimation);
        if(this.ReelID != 5) this.addChild(this._loopAnimation);
        this.addChild(this.spineAnimation);
        this.addChild(this._areaOverlayAnimation);

        // Loop
        this.idleAnimation = Sequence.createSequence(GameResources.getTextures("Reel6Idle"));
        this.idleAnimation.alpha = 0;
        this.addChild(this.idleAnimation);
    }

    private createGraphic():PIXI.Graphics{
        const graphicBg:PIXI.Graphics =  new PIXI.Graphics();
        graphicBg.beginFill(0x000000, 1);
        graphicBg.drawRect(-PunkSymbolData.DEFAULT_SYMBOL.DIM.WIDTH * 0.5, -PunkSymbolData.DEFAULT_SYMBOL.DIM.HEIGHT*1.5,
             PunkSymbolData.DEFAULT_SYMBOL.DIM.WIDTH, PunkEngineManager.ReelAreaHeight);
        graphicBg.endFill();
        this.addChild(graphicBg);
        return graphicBg;
    }


    public IdleEnabling(delay:number = 3):void{
        this.timelineIdle = new TimelineMax({
            onComplete:() => this.IdleEnabling(3 + Math.random()*3),
            onStart:() => this.idleAnimation.alpha = 1
        })
        this.idleAnimation.alpha = 0;
        this.timelineIdle.delay(delay);
        this.timelineIdle.add(this.idleAnimation.playAnimation());
        this.timelineIdle.play();
    }

    public IdleDisabling():void{
        if(this.timelineIdle){
            TweenLite.to(this.idleAnimation,0.25,{alpha:0}).eventCallback("onComplete",() => {
                Helper.timeLineKiller(this.timelineIdle);
                this.idleAnimation.alpha = 0;
            });
        }
    }

    public playBoom():TimelineMax{
        this._animationState.set(ReelAnimationState.BOOM);
        this._areaOverlayAnimation.setTexture(this.currentAnimationTexture);
        this.spineAnimation.visible = true;
        this._areaAnimation.scale = this.stateType;

        this.spineAnimation.state.setAnimation(0, 'Reel_6_Bending', false);
        PunkEngineManager.getReel(this._reelID).visible = true;
        PunkEngineManager.getReel(this._reelID).AllWindowSymbols.forEach(sym => { sym.setActive(false)});
        this._areaOverlayAnimation.alpha = 1;
        const tl:TimelineMax = new TimelineMax();
        tl.add(this._areaOverlayAnimation.playAnimation({onCompleteCallback: () => this._areaOverlayAnimation.alpha = 0}))

        tl.add(() => {
            this._areaAnimation.alpha = 0;
            this._areaAnimation.setTexture(GameResources.getTextures("Reel6_Placeholder"));

        }, this._areaOverlayAnimation.totalDuration*0.5)

        tl.to(this._graphicBg, this._areaOverlayAnimation.totalDuration*0.5,{alpha:0},this._areaOverlayAnimation.totalDuration*0.5)
        return tl;
    }

    public setActiveReel(value:boolean):void{
        PunkEngineManager.getReel(this._reelID).visible = value;
        if(this._reelID == 5){
            this._graphicBg.alpha = !value ? 1 : 0;
        } 
        this._areaAnimation.setTexture(GameResources.getTextures(ReelAnimationState.NORMAL));
        this._areaAnimation.setVisible(!value);
        if(this.timeline && this.timeline.isActive()) this.timeline.kill();
    }

    public showSkulls(skulls:number[], durr:number = 0.25):TimelineMax{
        const tl = new TimelineMax();
        for(let i = 0; i < this.skulls.length; i++){
            if(skulls.indexOf(i) >= 0){
                tl.fromTo(this.skulls[i].scale,durr,{x:0.8,y:0.8},{x:1,y:1,ease:Power2.easeIn},0);
                tl.fromTo(this.skulls[i],durr,{alpha: 0},{alpha: 1},0);
            }else{
                tl.fromTo(this.skulls[i].scale,durr,{x:1,y:1},{x:0.8,y:0.8,ease:Power2.easeOut},0);
                tl.to(this.skulls[i],durr,{alpha: 0},0); 
            }
        }

        if(!this._loopAnimation.isActive){
            this.activateLoop(ReelAnimationState.FIRE, MathHelper.frameToSeconds(6));
            tl.fromTo(this._areaAnimation,durr*0.5,{alpha:1},{alpha:0.45});
        }

        return tl;
    }

    public NumberOfSkulls(amount:number = 0):void{
        amount = Math.min(this.skulls.length,amount);
        for(let i = 0; i < amount; i++){
            if(this.skulls[i].alpha != 1){
                TweenLite.fromTo(this.skulls[i].scale,0.2,{x:0.8,y:0.8},{x:1,y:1,ease:Power2.easeIn});
                TweenLite.fromTo(this.skulls[i],0.2,{alpha: 0},{alpha: 1});
            }
        }

        if(amount >= 2) this.activateLoop(ReelAnimationState.FIRE, MathHelper.frameToSeconds(6));
        this._areaAnimation.alpha = 1 - amount*(0.55/4);
    }

    public removeSkulls(animate:boolean = true):void{
        if(animate){
            let skullsActive:number = 0;
            for(let i:number = 0; i < this.skulls.length; i++){
                if(this.skulls[i].alpha != 0){
                    skullsActive++;
                    TweenLite.fromTo(this.skulls[i].scale,0.25,{x:1,y:1},{x:0.8,y:0.8,ease:Power2.easeOut});
                    TweenLite.to(this.skulls[i],0.25,{alpha: 0}); 
                }
            }
            if(skullsActive >= 2) this.killLoop();
            TweenLite.to(this._areaAnimation,0.25,{alpha: 1})
        }else{
            for(let i:number = 0; i < this.skulls.length; i++){
                if(this.skulls[i].alpha != 0){
                    this.skulls[i].alpha = 0;
                }
            }
        }
    }

    public activateLoop(type:string,duration?:number,override?:boolean):TimelineMax{
        if(!override && this._animationState.equal(type)) return new TimelineMax();
        if(this.timeline && this.timeline.isActive()) this.timeline.kill();
        this._animationState.set(type);
        this._loopAnimation.setTexture(this.currentAnimationTexture);
        this._loopAnimation.blendMode = this.stateBlend;

        this.timeline = new TimelineMax({
            repeat: -1,
            repeatDelay: 0,
            onStart:() => { this._loopAnimation.alpha = 1;  }
        });

        this.timeline.add(this._loopAnimation.playAnimation({duration:duration}));
        return this.timeline;
    }

    public killLoop():void{
        if(this.timeline && this.timeline.isActive()) TweenLite.to(this._loopAnimation,0.3,{alpha:0}).eventCallback("onComplete",() => { this.timeline.kill(); })
    }

    public chainsActive(value:boolean):TimelineMax{
        if(value){
            this._animationState.set(ReelAnimationState.REEL_LOCK);
            this._areaAnimation.setTexture(this.currentAnimationTexture);
            this._areaAnimation.blendMode = this.stateBlend;
            this._areaAnimation.scale = this.stateType;
        }
        
        return this._areaAnimation.playAnimation({ 
            reverse:!value,
            onStart:() => {
                // if(value) SoundManager.playSoundEffect(SoundConfig.instance.CHAIN1);
                this._areaAnimation.setVisible(true);
                this._areaAnimation.alpha = 1;
            },
            onCompleteCallback:() => {
                this._areaAnimation.alpha = value ? 1 : 0;
                if(!value) this._animationState.set(ReelAnimationState.NORMAL)
        }});
    }

    public overlayGraphicAlpha(from:number = 0, to:number = 0, duration:number = 0.2):TimelineMax{
        const tl:TimelineMax = new TimelineMax();
        tl.fromTo(this._graphicBg,duration,{alpha:from},{alpha:to});
        return tl;
    }

    public startAnimation(type:string):TimelineMax{
        this._areaAnimation.setTexture(GameResources.getTextures(type));
        this._areaAnimation.blendMode = BLEND_MODES.ADD;
        this._areaAnimation.scale = new PIXI.Point(2,2);
        this._areaAnimation.alpha = 0;
        return this._areaAnimation.playAnimation({onStart:() => this._areaAnimation.alpha = 0.25, onCompleteCallback:() => { this._areaAnimation.alpha = 0;}});
    }

    public showAnimation(type:string):TimelineMax{
        if(this._animationState.equal(type)) return new TimelineMax({
            onStart:() => this._areaAnimation.setVisible(true)
        });
        this._animationState.set(type);
        this._areaAnimation.setTexture(this.currentAnimationTexture);
        this._areaAnimation.blendMode = this.stateBlend;
        this._areaAnimation.scale = this.stateType;
        this._areaAnimation.alpha = 1;

        return this._areaAnimation.playAnimation({ onCompleteCallback:() => {
            if(!this._animationState.equal(ReelAnimationState.NORMAL)) this._areaAnimation.alpha = 0;
        }});
    }

    public showReelGlow(active:boolean = false,duration:number = 1):TimelineMax{
        if(!active){
            if(this.timeline && this.timeline.isActive()){
                TweenLite.to(this._loopAnimation, duration,{alpha:0}).eventCallback("onComplete",() => { 
                    this.timeline.kill();
                    this._loopAnimation.alpha = 0;
                });
            }
            return this.timeline;
        }

        this._animationState.set(ReelAnimationState.REEL_FEATURE_ATTENTION);
        this._loopAnimation.setTexture(this.currentAnimationTexture);
        this._loopAnimation.blendMode = this.stateBlend;
        this._loopAnimation.alpha = 0;
        this.timeline = new TimelineMax({
            repeat: -1,
            repeatDelay: 0,
        });
        this.timeline.to(this._loopAnimation, duration*0.5,{alpha: 1});
        this.timeline.to(this._loopAnimation, duration*0.5,{alpha: 0});
        return this.timeline;
    }

    public playEnd():TimelineMax{
        this._areaAnimation.setTexture(GameResources.getTextures(ReelAnimationState.END));
        this._areaAnimation.scale = this.stateType;
        const tl:TimelineMax = new TimelineMax({
            onStart:() => {
                this._areaAnimation.setVisible(true);
                this._areaAnimation.alpha = 1;
                this._areaOverlayAnimation.alpha = 0;
                this._loopAnimation.alpha = 0;
            },
            onComplete: () => { 
                this.setActiveReel(false);
                this._animationState.set(ReelAnimationState.NORMAL);
                this._areaAnimation.setTexture(GameResources.getTextures(ReelAnimationState.NORMAL));
        }});

        tl.add(this._areaAnimation.playAnimation());
        tl.play();
        return tl;
    }

    public onResize(){
        this.position = MathHelper.pointAdd(PunkEngineManager.getReelPos(this._reelID), new Point(0,PunkSymbolData.DEFAULT_SYMBOL.DIM.HEIGHT));// MathHelper.pointAdd(PunkEngineManager.getReel(this._reelID).position as Point, new Point(0,(PunkGameConfig.instance.REEL_ENGINE_CONFIG.STEP_DISTANCE * PunkEngineManager.getReel(this._reelID).scale.y)-1));
        this.scale = PunkEngineManager.dim.scale;
    }
}